import { useMemo } from "react";
import { useTheme } from "context/ThemeContext";

// Turns an API-supplied colour code (e.g. a knowledge-base chapter's
// `color_code`) into a small set of theme-safe CSS values.
//
// Why this exists: raw hex straight from the DB is unpredictable. A pale
// yellow is invisible as text on a light surface, a navy blue disappears on a
// dark one, and `${hex}1a` string concatenation silently breaks for any
// non-6-digit input. Everything below normalises the hue into a band that
// stays readable in both themes, while fills stay as alpha over whatever
// surface they sit on.

export const DEFAULT_ACCENT = "#6366F1";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// Accepts "#abc", "#aabbcc", "rgb(r,g,b)" and "rgba(r,g,b,a)".
const parseColor = (value) => {
  if (typeof value !== "string") return null;
  const raw = value.trim();

  const hex = raw.replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    return hex.split("").map((char) => parseInt(char + char, 16));
  }
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
  }

  const match = raw.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
  if (match) return [Number(match[1]), Number(match[2]), Number(match[3])];

  return null;
};

const rgbToHsl = ([r, g, b]) => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  const l = (max + min) / 2;

  if (delta === 0) return [0, 0, l];

  const s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let h;
  if (max === rn) h = ((gn - bn) / delta + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / delta + 2) / 6;
  else h = ((rn - gn) / delta + 4) / 6;

  return [h, s, l];
};

/**
 * @param {string} colorCode  raw colour from the API
 * @param {boolean} isDark    current theme
 * @returns {{ base: string, text: string, fill: string, softFill: string, border: string }}
 *   base     — the colour as given; use for solid accent bars/spines
 *   text     — lightness-clamped so it stays legible on the theme's surface
 *   fill     — chip / badge background
 *   softFill — large tinted areas (image placeholders)
 *   border   — hairlines and dividers
 */
export const getAccentColor = (colorCode, isDark = false) => {
  const rgb = parseColor(colorCode) || parseColor(DEFAULT_ACCENT);
  const [r, g, b] = rgb;
  const [h, s, l] = rgbToHsl(rgb);

  // Greys stay grey; anything with colour keeps enough saturation to register.
  const saturation = s === 0 ? 0 : clamp(s, 0.35, 1);
  const lightness = isDark ? clamp(l, 0.62, 0.84) : clamp(l, 0.28, 0.46);

  const rgba = (alpha) => `rgba(${r}, ${g}, ${b}, ${alpha})`;

  return {
    base: `rgb(${r}, ${g}, ${b})`,
    text: `hsl(${Math.round(h * 360)}, ${Math.round(saturation * 100)}%, ${Math.round(lightness * 100)}%)`,
    fill: rgba(isDark ? 0.2 : 0.12),
    softFill: rgba(isDark ? 0.14 : 0.09),
    border: rgba(isDark ? 0.45 : 0.35),
  };
};

// Hook form — resolves the current theme for you.
export const useAccentColor = (colorCode) => {
  const theme = useTheme();
  const isDark = !!theme?.isDark;
  return useMemo(() => getAccentColor(colorCode, isDark), [colorCode, isDark]);
};
